import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Button,
  Spinner,
  Box,
  Divider,
  TextField,
  ProgressBar,
  DescriptionList,
  Modal,
  TextContainer
} from '@shopify/polaris';
import {
  EmailMajor,
  PackageMajor,
  LocationMajor,
  CustomersMajor,
  CalendarMajor,
  EditMajor
} from '@shopify/polaris-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import api from '../utils/axiosConfig';

// Tracking status to percentage mapping
const trackingStatusToPercentage = {
  created: 10,
  registered: 30,
  received: 50,
  transit: 70,
  delivered: 100,
  warning: -1
};

function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [emailModalActive, setEmailModalActive] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    if (id) {
      loadOrderDetails();
    }
  }, [id]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/order-details/${id}`);
      setOrder(response.data);
      setCustomerEmail(response.data.customer?.email || '');
      
    } catch (err) {
      console.error('Error loading order details:', err);
      setError(err.response?.data?.error || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const getFinancialStatusBadge = (status) => {
    const statusMap = {
      paid: { status: 'success', children: t('orderDetails.paymentStatus.paid') },
      pending: { status: 'warning', children: t('orderDetails.paymentStatus.pending') },
      refunded: { status: 'critical', children: t('orderDetails.paymentStatus.refunded') },
      partially_refunded: { status: 'warning', children: t('orderDetails.paymentStatus.partially_refunded') }
    };
    
    const config = statusMap[status] || { status: 'default', children: status };
    return <Badge {...config} />;
  };

  const getOrderStatusBadge = (status) => {
    const statusMap = {
      fulfilled: { status: 'success', children: t('orderDetails.orderStatus.fulfilled') },
      unfulfilled: { status: 'warning', children: t('orderDetails.orderStatus.unfulfilled') },
      partial: { status: 'attention', children: t('orderDetails.orderStatus.partial') },
      delivered: { status: 'success', children: t('orderDetails.orderStatus.delivered') }
    };
    
    const config = statusMap[status] || { status: 'default', children: status };
    return <Badge {...config} />;
  };

  const getTrackingPercentage = () => {
    if (!order?.tracking?.tracking_status_synced) return 0;
    return trackingStatusToPercentage[order.tracking.tracking_status_synced] || 0;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const locale = i18n.language === 'pt' ? ptBR : enUS;
      const dateFormat = i18n.language === 'pt' ? "dd/MM/yyyy 'às' HH:mm" : "MM/dd/yyyy 'at' HH:mm";
      return format(parseISO(date), dateFormat, { locale });
    } catch {
      return t('orderDetails.invalidDate');
    }
  };

  const formatRelativeDate = (date) => {
    if (!date) return '';
    try {
      const locale = i18n.language === 'pt' ? ptBR : enUS;
      return formatDistanceToNow(parseISO(date), { locale, addSuffix: true });
    } catch {
      return '';
    }
  };

  const handleSaveEmail = async () => {
    if (!customerEmail || !customerEmail.includes('@')) {
      setEmailError(t('orderDetails.emailError'));
      return;
    }

    try {
      setSavingEmail(true);
      setEmailError('');
      
      await api.put(`/order-details/${id}/customer-email`, {
        email: customerEmail
      });
      
      // Reload order to get updated data
      await loadOrderDetails();
      setEditingEmail(false);
      
    } catch (err) {
      console.error('Error updating email:', err);
      setEmailError(t('orderDetails.emailUpdateError'));
    } finally {
      setSavingEmail(false);
    }
  };

  const getJourneyStatusBadge = (status) => {
    const statusMap = {
      active: { status: 'success', children: t('orderDetails.journeyStatus.active') },
      paused: { status: 'warning', children: t('orderDetails.journeyStatus.paused') },
      completed: { status: 'success', children: t('orderDetails.journeyStatus.completed') },
      cancelled: { status: 'critical', children: t('orderDetails.journeyStatus.cancelled') },
      not_started: { status: 'default', children: t('orderDetails.journeyStatus.not_started') }
    };
    
    const config = statusMap[status] || { status: 'default', children: status };
    return <Badge {...config} />;
  };

  if (loading) {
    return (
      <Page
        title={t('orderDetails.loading')}
        breadcrumbs={[{ content: t('orderDetails.breadcrumb'), url: '/orders' }]}
      >
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <Box padding="8">
                <div style={{ textAlign: 'center' }}>
                  <Spinner size="large" />
                </div>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error || !order) {
    return (
      <Page
        title={t('orderDetails.error')}
        breadcrumbs={[{ content: t('orderDetails.breadcrumb'), url: '/orders' }]}
      >
        <Layout>
          <Layout.Section>
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <p style={{ color: '#dc2626', margin: '0' }}>{error || t('orderDetails.orderNotFound')}</p>
            </div>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const trackingPercentage = getTrackingPercentage();

  return (
    <Page
      title={`Pedido ${order.shopify_order_name || order.id}`}
      breadcrumbs={[{ content: t('orderDetails.breadcrumb'), url: '/orders' }]}
      subtitle={`${order.store?.name || order.store?.domain || 'Loja desconhecida'}`}
    >
      <Layout>
        {/* Order Summary */}
        <Layout.Section>
          <Card sectioned>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                <div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                    <Text variant="headingMd" as="h2">
                      {order.shopify_order_name}
                    </Text>
                    {getFinancialStatusBadge(order.financial_status)}
                    {getOrderStatusBadge(order.status)}
                  </div>
                  <Text variant="bodyMd" color="subdued">
                    {t('orderDetails.createdOn')} {formatDate(order.created_at)}
                  </Text>
                </div>
                <Text variant="headingLg" as="p">
                  {order.currency} {order.total_price?.toFixed(2)}
                </Text>
              </div>
            </div>

            <Divider />

            {/* Tracking Progress */}
            {order.tracking && (
              <Box paddingBlockStart="4">
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <Text variant="bodyMd" fontWeight="semibold">
                      {t('orderDetails.deliveryStatus')}
                    </Text>
                    <Text variant="bodyMd">
                      {trackingPercentage}{t('orderDetails.percentComplete')}
                    </Text>
                  </div>
                  <ProgressBar 
                    progress={trackingPercentage} 
                    status={trackingPercentage === 100 ? 'success' : 'primary'}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {order.tracking.tracking_code_generated && (
                      <Text variant="bodySm" color="subdued">
                        {t('orderDetails.code')}: {order.tracking.tracking_code_generated}
                        {order.tracking.carrier && ` • ${order.tracking.carrier}`}
                      </Text>
                    )}
                    {order.tracking.external_tracking_number && (
                      <Text variant="bodySm" color="subdued">
                        {t('orderDetails.carrierTracking')}: {order.tracking.external_tracking_number}
                      </Text>
                    )}
                  </div>
                </div>
              </Box>
            )}
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Layout>
            {/* Left Column */}
            <Layout.Section oneHalf>
              {/* Customer Information */}
              <Card sectioned>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <Text variant="headingMd" as="h3">
                      👤 {t('orderDetails.customerInfo')}
                    </Text>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {order.customer ? (
                      <>
                        <Text variant="bodyMd" fontWeight="semibold">
                          {order.customer.name || t('orderDetails.customerNotProvided')}
                        </Text>
                        {order.customer.company && (
                          <Text variant="bodyMd" color="subdued">
                            {order.customer.company}
                          </Text>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {editingEmail ? (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                              <TextField
                                value={customerEmail}
                                onChange={setCustomerEmail}
                                type="email"
                                error={emailError}
                                autoComplete="off"
                              />
                              <Button primary onClick={handleSaveEmail} loading={savingEmail}>
                                {t('orderDetails.save')}
                              </Button>
                              <Button onClick={() => {
                                setEditingEmail(false);
                                setCustomerEmail(order.customer?.email || '');
                                setEmailError('');
                              }}>
                                {t('orderDetails.cancel')}
                              </Button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Text variant="bodyMd">
                                {order.customer.email}
                              </Text>
                              <Button plain icon={EditMajor} onClick={() => setEditingEmail(true)}>
                                {t('orderDetails.edit')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <Text variant="bodyMd" color="subdued">
                        {t('orderDetails.customerNotProvided')}
                      </Text>
                    )}
                  </div>
                </div>
              </Card>

              {/* Shipping Address */}
              <Card sectioned>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <Text variant="headingMd" as="h3">
                      📍 {t('orderDetails.shippingAddress')}
                    </Text>
                  </div>
                  
                  {order.shipping_address ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {order.shipping_address.company && (
                        <Text variant="bodyMd" fontWeight="semibold">
                          {order.shipping_address.company}
                        </Text>
                      )}
                      <Text variant="bodyMd">
                        {order.shipping_address.address1}
                      </Text>
                      {order.shipping_address.address2 && (
                        <Text variant="bodyMd">
                          {order.shipping_address.address2}
                        </Text>
                      )}
                      <Text variant="bodyMd">
                        {order.shipping_address.city}, {order.shipping_address.province}
                      </Text>
                      <Text variant="bodyMd">
                        {order.shipping_address.country} - CEP: {order.shipping_address.zip}
                      </Text>
                      {order.shipping_address.phone && (
                        <Text variant="bodyMd">
                          {t('orderDetails.phone')}: {order.shipping_address.phone}
                        </Text>
                      )}
                    </div>
                  ) : (
                    <Text variant="bodyMd" color="subdued">
                      {t('orderDetails.addressNotProvided')}
                    </Text>
                  )}
                </div>
              </Card>
            </Layout.Section>

            {/* Right Column */}
            <Layout.Section oneHalf>
              {/* Email Journey Status */}
              <Card sectioned>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text variant="headingMd" as="h3">
                        📧 {t('orderDetails.emailJourney')}
                      </Text>
                    </div>
                    {getJourneyStatusBadge(order.email_journey?.status || 'not_started')}
                  </div>
                  
                  {order.email_journey && order.email_journey.id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text variant="bodyMd">{t('orderDetails.progress')}</Text>
                        <Text variant="bodyMd">
                          {t('orderDetails.dayOf')} {order.email_journey.current_day} {t('orderDetails.of')} 20
                        </Text>
                      </div>
                      
                      <ProgressBar 
                        progress={(order.email_journey.current_day / 20) * 100}
                        status={order.email_journey.status === 'completed' ? 'success' : 'primary'}
                      />
                      
                      <DescriptionList
                        items={[
                          {
                            term: t('orderDetails.lastEmail'),
                            description: order.email_journey.last_email_sent 
                              ? formatDate(order.email_journey.last_email_sent)
                              : t('orderDetails.noEmailSent')
                          },
                          {
                            term: t('orderDetails.nextEmail'),
                            description: order.email_journey.next_email_date 
                              ? formatDate(order.email_journey.next_email_date)
                              : t('orderDetails.notScheduled')
                          }
                        ]}
                      />
                      
                      {order.email_journey.error_count > 0 && (
                        <div style={{
                          backgroundColor: '#fffbeb',
                          border: '1px solid #f59e0b',
                          borderRadius: '6px',
                          padding: '16px'
                        }}>
                          <p style={{ color: '#92400e', margin: '0' }}>{order.email_journey.error_count} {t('orderDetails.errorsFound')}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Text variant="bodyMd" color="subdued">
                        {t('orderDetails.noJourneyStarted')}
                      </Text>
                      <Button primary onClick={() => setEmailModalActive(true)}>
                        {t('orderDetails.startJourney')}
                      </Button>
                    </div>
                  )}
                </div>
              </Card>

              {/* Important Dates */}
              <Card sectioned>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <Text variant="headingMd" as="h3">
                      📅 {t('orderDetails.importantDates')}
                    </Text>
                  </div>
                  
                  <DescriptionList
                    items={[
                      {
                        term: t('orderDetails.createdOn'),
                        description: order.created_at 
                          ? `${formatDate(order.created_at)} (${formatRelativeDate(order.created_at)})`
                          : 'N/A'
                      },
                      {
                        term: t('orderDetails.updatedOn'),
                        description: order.updated_at 
                          ? `${formatDate(order.updated_at)} (${formatRelativeDate(order.updated_at)})`
                          : 'N/A'
                      },
                      {
                        term: t('orderDetails.deliveredOn'),
                        description: order.delivered_at 
                          ? `${formatDate(order.delivered_at)} (${formatRelativeDate(order.delivered_at)})`
                          : t('orderDetails.notDeliveredYet')
                      },
                      {
                        term: t('orderDetails.trackingExpectedDate'),
                        description: order.tracking_expected_date 
                          ? formatDate(order.tracking_expected_date)
                          : t('orderDetails.notEstimated')
                      }
                    ]}
                  />
                </div>
              </Card>
            </Layout.Section>
          </Layout>
        </Layout.Section>

        {/* Tracking Events */}
        {order.tracking?.events && order.tracking.events.length > 0 && (
          <Layout.Section>
            <Card sectioned>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <Text variant="headingMd" as="h3">
                    📦 {t('orderDetails.trackingHistory')}
                  </Text>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {order.tracking.events.map((event, index) => (
                    <Box key={event.id || index} padding="3" background="subdued">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <Text variant="bodyMd" fontWeight="semibold">
                            {event.event_detail}
                          </Text>
                          {event.address && (
                            <Text variant="bodySm" color="subdued">
                              {event.address}
                            </Text>
                          )}
                        </div>
                        <Text variant="bodySm" color="subdued">
                          {formatDate(event.event_time)}
                        </Text>
                      </div>
                    </Box>
                  ))}
                </div>
              </div>
            </Card>
          </Layout.Section>
        )}
      </Layout>

      {/* Start Email Journey Modal */}
      <Modal
        open={emailModalActive}
        onClose={() => setEmailModalActive(false)}
        title={t('orderDetails.startEmailJourney')}
        primaryAction={{
          content: t('orderDetails.start'),
          onAction: async () => {
            try {
              await api.post('/email-journeys/start', {
                orderId: order.id,
                customerEmail: order.customer?.email
              });
              setEmailModalActive(false);
              loadOrderDetails();
            } catch (err) {
              console.error('Error starting journey:', err);
            }
          }
        }}
        secondaryActions={[
          {
            content: t('orderDetails.cancel'),
            onAction: () => setEmailModalActive(false)
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              {t('orderDetails.startJourneyDescription')}
              {order.customer?.email && ` (${order.customer.email})`}.
            </p>
            <p>
              {t('orderDetails.journeyConsistsOf')}
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

export default OrderDetails;